import { API_URL } from './config';

export interface AcademyLesson {
    id: string;
    club_id: string;
    title: string;
    description: string;
    type: 'PRIVATE' | 'GROUP' | 'CLINIC';
    coverImage?: string;
    levelMin?: number;
    levelMax?: number;
    level?: string; // Gardé pour compatibilité descendante
    date: number; // Unix timestamp
    duration_minutes: number;
    price_cents: number;
    currency: string;
    max_participants: number;
    current_participants: number;
    courtIds: string[]; // JSON array of strings
    coach_id?: string;
    preRegisteredUsers?: any[];
    club_name?: string;
    club_city?: string;
    acceptPaymentOnSite?: boolean;
}

export interface ClubCoach {
    id: string;
    club_id: string;
    user_id: string;
    name: string;
    title: string;
    bio?: string;
    photo_url?: string;
}

export const academyService = {
    // Pro endpoints
    listClubLessons: async (clubId: string): Promise<AcademyLesson[]> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/academy/lessons`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch club lessons");
        return res.json();
    },

    createLesson: async (clubId: string, data: Partial<AcademyLesson>): Promise<{ lessonId: string }> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/academy/lessons`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to create lesson");
        }
        return res.json();
    },

    updateLesson: async (clubId: string, lessonId: string, data: Partial<AcademyLesson>): Promise<{ lessonId: string }> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/academy/lessons/${lessonId}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to update lesson");
        }
        return res.json();
    },

    deleteLesson: async (clubId: string, lessonId: string): Promise<any> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/academy/lessons/${lessonId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to delete lesson");
        }
        return res.json();
    },

    getClubCoaches: async (clubId: string): Promise<ClubCoach[]> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/clubs/${clubId}/academy/coaches`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed to fetch club coaches");
        return res.json();
    },

    // Public / App endpoints
    searchLessons: async (query?: { country?: string; limit?: number }): Promise<AcademyLesson[]> => {
        const params = new URLSearchParams();
        if (query?.country) params.append('country', query.country);
        if (query?.limit) params.append('limit', query.limit.toString());

        const res = await fetch(`${API_URL}/academy/search?${params.toString()}`);
        if (!res.ok) throw new Error("Failed to search lessons");
        return res.json();
    },

    checkoutLesson: async (lessonId: string, equipmentIds?: string[]): Promise<{ url: string }> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/academy/lessons/${lessonId}/checkout`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ equipmentIds: equipmentIds || [] })
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to initiate checkout");
        }
        return res.json();
    },

    joinLessonOnSite: async (lessonId: string): Promise<{ success: boolean; id: string }> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/academy/lessons/${lessonId}/join`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to join lesson");
        }
        return res.json();
    },

    leaveLesson: async (lessonId: string): Promise<{ success: boolean }> => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/academy/lessons/${lessonId}/leave`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to leave lesson");
        }
        return res.json();
    }
};
